import { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Autocomplete,
  Alert, CircularProgress, Typography, Table, TableHead, TableRow, TableCell, TableBody, Checkbox, Chip,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createPayment, allocatePayment, executePayment, cancelPayment, getPayment, getSupplierOpenInvoices, getSupplierAdvanceBalance, listCashAccounts,
  type PaymentListItem, type SavePaymentLinePayload,
} from '../../api/financiar'
import { listSuppliers } from '../../api/furnizori'
import { errorMessage } from '../../api/client'

export default function PaymentDialog({ target, onClose }: { target: PaymentListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const [supplierId, setSupplierId] = useState('')
  const [cashAccountId, setCashAccountId] = useState('')
  const [currency, setCurrency] = useState('RON')
  const [exchangeRate, setExchangeRate] = useState('1')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [notes, setNotes] = useState('')
  const [lineAmounts, setLineAmounts] = useState<Record<string, string>>({})
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  useEffect(() => {
    if (target === 'new') {
      setSupplierId(''); setCashAccountId(''); setCurrency('RON'); setExchangeRate('1'); setAmount(''); setCategory('');
      setDate(new Date().toISOString().slice(0, 10)); setNotes(''); setLineAmounts({});
    }
    setPendingMessage(null)
  }, [target])

  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open && !editing })
  const { data: accounts } = useQuery({ queryKey: ['financiar', 'conturi'], queryFn: listCashAccounts, enabled: open && !editing })
  const { data: detail } = useQuery({ queryKey: ['financiar', 'plata', editing ? (target as PaymentListItem).id : null], queryFn: () => getPayment((target as PaymentListItem).id), enabled: editing })
  const effectiveSupplierId = editing ? detail?.supplierId : supplierId
  const { data: openInvoices } = useQuery({
    queryKey: ['financiar', 'facturi-furnizor-deschise', effectiveSupplierId], queryFn: () => getSupplierOpenInvoices(effectiveSupplierId!), enabled: !!effectiveSupplierId,
  })
  const { data: advance } = useQuery({
    queryKey: ['financiar', 'sold-avans-furnizor', effectiveSupplierId], queryFn: () => getSupplierAdvanceBalance(effectiveSupplierId!), enabled: !!effectiveSupplierId,
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['financiar', 'plati'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'plata'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'facturi-furnizor-deschise'] })
    void qc.invalidateQueries({ queryKey: ['financiar', 'sold-avans-furnizor'] })
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const lines: SavePaymentLinePayload[] = Object.entries(lineAmounts).filter(([, v]) => Number(v) > 0)
        .map(([supplierInvoiceId, v]) => ({ supplierInvoiceId, amount: Number(v) }))
      return createPayment({ date, supplierId, cashAccountId, currency, exchangeRate: Number(exchangeRate), amount: Number(amount), category: category || null, notes: notes || null, lines })
    },
    onSuccess: () => { invalidate(); onClose() },
  })
  const allocateMutation = useMutation({
    mutationFn: () => {
      const lines: SavePaymentLinePayload[] = Object.entries(lineAmounts).filter(([, v]) => Number(v) > 0)
        .map(([supplierInvoiceId, v]) => ({ supplierInvoiceId, amount: Number(v) }))
      return allocatePayment((target as PaymentListItem).id, lines)
    },
    onSuccess: () => { invalidate(); setLineAmounts({}) },
  })
  const executeMutation = useMutation({
    mutationFn: () => executePayment((target as PaymentListItem).id),
    onSuccess: (r) => { invalidate(); if (r.pendingApproval) setPendingMessage('Plata a fost trimisă la aprobare.') },
  })
  const cancelMutation = useMutation({
    mutationFn: () => cancelPayment((target as PaymentListItem).id),
    onSuccess: () => { invalidate(); onClose() },
  })

  const alreadyAllocatedIds = new Set(detail?.allocations.map((a) => a.supplierInvoiceId) ?? [])
  const availableInvoices = (openInvoices ?? []).filter((i) => !alreadyAllocatedIds.has(i.supplierInvoiceId))
  const totalAmount = editing ? (detail?.amount ?? 0) : Number(amount) || 0
  const alreadyAllocated = detail?.allocations.reduce((s, a) => s + a.allocatedAmount, 0) ?? 0
  const remaining = totalAmount - alreadyAllocated - Object.values(lineAmounts).reduce((s, v) => s + (Number(v) || 0), 0);
  const isDraft = !editing || detail?.status === 'Draft'

  if (editing && !detail) {
    return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth><DialogContent><Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack></DialogContent></Dialog>
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Plată ${detail?.number}` : 'Plată nouă'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {editing && detail?.status !== 'Draft' && (
            <Alert severity={detail?.status === 'Executata' ? 'success' : 'warning'}>
              {detail?.status === 'Executata' ? `Executată la ${detail.executedAt}.` : 'Anulată.'}
            </Alert>
          )}
          {!editing ? (
            <>
              <Autocomplete
                options={suppliers?.map((s) => s.id) ?? []}
                getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
                value={supplierId || null} onChange={(_, v) => { setSupplierId(v ?? ''); setLineAmounts({}) }}
                renderInput={(params) => <TextField {...params} label="Furnizor" />}
              />
              <Stack direction="row" spacing={2}>
                <TextField select label="Cont" value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)} fullWidth>
                  {accounts?.map((a) => <MenuItem key={a.id} value={a.id}>{a.name} ({a.currency})</MenuItem>)}
                </TextField>
                <TextField type="date" label="Dată" value={date} onChange={(e) => setDate(e.target.value)} sx={{ width: 170 }} slotProps={{ inputLabel: { shrink: true } }} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 110 }} />
                {currency !== 'RON' && <TextField type="number" label="Curs/RON" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} sx={{ width: 140 }} />}
                <TextField type="number" label="Sumă" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth />
              </Stack>
              <TextField label="Categorie" value={category} onChange={(e) => setCategory(e.target.value)} fullWidth />
              <TextField label="Note" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Furnizor: {detail?.supplierName ?? '—'} · Cont: {detail?.cashAccountName} · Dată: {detail?.date} · Sumă: {detail?.amount.toFixed(2).replace('.', ',')} {detail?.currency}
              {detail?.isRecurring && <> · <Chip size="small" label="generată din programare recurentă" /></>}
            </Typography>
          )}

          {advance && advance.balance > 0 && <Alert severity="info">Sold de avans la acest furnizor: {advance.balance.toFixed(2).replace('.', ',')}.</Alert>}

          {editing && detail && detail.allocations.length > 0 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>Factură furnizor</TableCell><TableCell align="right">Alocat</TableCell></TableRow></TableHead>
              <TableBody>
                {detail.allocations.map((a) => (
                  <TableRow key={a.id}><TableCell>{a.supplierInvoiceNumber}</TableCell><TableCell align="right">{a.allocatedAmount.toFixed(2).replace('.', ',')}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {isDraft && effectiveSupplierId && availableInvoices.length > 0 && (
            <>
              <Typography variant="subtitle2">Alocare pe facturi furnizor aprobate</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell padding="checkbox" /><TableCell>Factură</TableCell><TableCell align="right">Sold rămas</TableCell><TableCell align="right">Alocă</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {availableInvoices.map((inv) => (
                    <TableRow key={inv.supplierInvoiceId}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!lineAmounts[inv.supplierInvoiceId]}
                          onChange={(e) => setLineAmounts((m) => ({ ...m, [inv.supplierInvoiceId]: e.target.checked ? String(Math.min(inv.outstanding, Math.max(remaining, 0) || inv.outstanding)) : '' }))} />
                      </TableCell>
                      <TableCell>{inv.number} <Chip size="small" label={`scad. ${inv.dueDate}`} /></TableCell>
                      <TableCell align="right">{inv.outstanding.toFixed(2).replace('.', ',')}</TableCell>
                      <TableCell align="right">
                        <TextField type="number" size="small" sx={{ width: 100 }} disabled={!lineAmounts[inv.supplierInvoiceId]}
                          value={lineAmounts[inv.supplierInvoiceId] ?? ''} onChange={(e) => setLineAmounts((m) => ({ ...m, [inv.supplierInvoiceId]: e.target.value }))} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary">Nealocat: {remaining.toFixed(2).replace('.', ',')}</Typography>
            </>
          )}

          {pendingMessage && <Alert severity="info">{pendingMessage}</Alert>}
          {(createMutation.isError || allocateMutation.isError || executeMutation.isError || cancelMutation.isError) && (
            <Alert severity="error">{errorMessage(createMutation.error ?? allocateMutation.error ?? executeMutation.error ?? cancelMutation.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
        {editing && isDraft && (
          <>
            {Object.values(lineAmounts).some((v) => Number(v) > 0) && (
              <Button onClick={() => allocateMutation.mutate()} disabled={allocateMutation.isPending}>Salvează alocarea</Button>
            )}
            <Button color="error" onClick={() => cancelMutation.mutate()} disabled={cancelMutation.isPending}>Anulează</Button>
            <Button variant="contained" onClick={() => executeMutation.mutate()} disabled={executeMutation.isPending}>Execută plata</Button>
          </>
        )}
        {!editing && (
          <Button variant="contained" disabled={!supplierId || !cashAccountId || !amount || createMutation.isPending}
            startIcon={createMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => createMutation.mutate()}>
            Salvează
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
